import type {
  AdminFoodRequest,
  AdminFoodRequestSource,
  FoodRequestKindName,
  FoodRequestOriginName,
  FoodResearchFieldName,
  FoodSourceName,
  RecipeConfidenceName,
} from '@steado/admin-contract/foods';

import type { BadgeTone } from '@/components/ui/badge';
import type { ToneName } from '@/components/table/tone';

/** How the review screens name things: kinds, confidence, origins, ages and sources. */

export const KIND_LABELS: Readonly<Record<FoodRequestKindName, string>> = {
  new_food: 'New food',
  name: 'Name',
  fix: 'Fix',
};

/** Violet for a new food, blue for a name, amber for a fix: the queue reads at a glance. */
export const KIND_TONES: Readonly<Record<FoodRequestKindName, BadgeTone>> = {
  new_food: 'purple',
  name: 'info',
  fix: 'warn',
};

/** "Fix request · 1 of 3". */
export const KIND_REQUEST: Readonly<Record<FoodRequestKindName, string>> = {
  new_food: 'New food request',
  name: 'Name request',
  fix: 'Fix request',
};

export type ConfidenceLevel = RecipeConfidenceName | 'none';

/** One word for a request or a field the research did not grade, in the queue and on the review. */
export const CONFIDENCE_LABELS: Readonly<Record<ConfidenceLevel, string>> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  none: 'Not graded',
};

export const CONFIDENCE_TONES: Readonly<Record<ConfidenceLevel, ToneName>> = {
  high: 'ok',
  medium: 'warn',
  low: 'err',
  none: 'mute',
};

/** Where a request came from, as the queue's sub-line says it. */
export const ORIGIN_LABELS: Readonly<Record<FoodRequestOriginName, string>> = {
  user: 'Asked in the app',
  trainer: 'Trainer',
  typed_meal: 'Typed meals',
  coach: 'Coach chat',
  photo_scan: 'Photo scans',
};

/** "from typed meals". */
export const ORIGIN_FROM: Readonly<Record<FoodRequestOriginName, string>> = {
  user: 'asked for in the app',
  trainer: 'asked for by a trainer',
  typed_meal: 'from typed meals',
  coach: 'from coach chat',
  photo_scan: 'from photo scans',
};

export const SOURCE_NAMES: Readonly<Record<FoodSourceName, string>> = {
  indb: 'Indian food database (INDB)',
  ifct: 'Indian food composition tables (IFCT)',
  open_food_facts: 'Open Food Facts',
  ai_estimated: 'AI estimate',
  manual: 'Manual',
  gym: 'A gym',
};

export const RESEARCH_FIELD_LABELS: Readonly<Record<FoodResearchFieldName, string>> = {
  nutrition: 'nutrition',
  portions: 'portions',
  recipe: 'recipe',
};

export const FOOD_STATE_LABELS: Readonly<Record<AdminFoodRequestSource['foodState'], string>> = {
  raw: 'Raw',
  cooked: 'Cooked, as eaten',
  as_sold: 'As sold',
  unknown: 'Raw or cooked not stated',
};

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

/** "5 h", "2 d", "40 min": how long a request has waited, in the coarsest true unit. */
export function waited(iso: string, now: Date = new Date()): { num: string; unit: string } {
  const minutes = Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000));
  if (minutes < 60) return { num: String(Math.max(1, minutes)), unit: 'min' };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { num: String(hours), unit: 'h' };
  return { num: String(Math.floor(hours / 24)), unit: 'd' };
}

/** "5 h ago". */
export function ago(iso: string, now: Date = new Date()): string {
  const { num, unit } = waited(iso, now);
  return `${num} ${unit} ago`;
}

/** "3 days": the oldest open request, in the summary line. */
export function daysWaiting(iso: string, now: Date = new Date()): string {
  const { num, unit } = waited(iso, now);
  if (unit === 'd') return plural(Number(num), 'day');
  if (unit === 'h') return plural(Number(num), 'hour');
  return plural(Number(num), 'minute');
}

/** "11:42", on the 24-hour clock the console uses. */
export function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** A figure as the console shows it: whole above 100, one decimal below. */
export function figure(value: number): string {
  if (Math.abs(value) >= 100) return String(Math.round(value));
  return String(Math.round(value * 10) / 10);
}

/**
 * The confidence a request shows, in the queue and the review header alike: the server's
 * (the lowest grade among its graded fields), none when nothing was graded.
 */
export function confidenceOf(request: AdminFoodRequest): ConfidenceLevel {
  return request.draftConfidence ?? 'none';
}

/** The line under a row's confidence: what is left to look at. */
export function checksOf(request: AdminFoodRequest): string {
  if (request.draftStatus === 'failed') return 'No sources';
  if (request.kind === 'name')
    return request.readyToApprove ? 'Nothing to check' : 'Another food has this name';
  if (request.draftStatus === 'queued') return 'Being researched';
  if (request.draftStatus !== 'ready' || request.toCheck === null) return 'Not researched';
  if (request.toCheck === 0) return request.kind === 'fix' ? 'No proposal' : 'Nothing to check';
  return `${String(request.toCheck)} to check`;
}

/** The line under "What the AI proposes". */
export function proposalSubOf(request: AdminFoodRequest): string {
  if (request.draftStatus === 'failed') return 'Nothing to review yet. Retry to research it again.';
  switch (request.kind) {
    case 'name':
      return `Everyone who typed it got ${request.foodName ?? 'the food'}; only the name is missing`;
    case 'new_food':
      return request.foodId === null
        ? 'It could not be estimated, so it was not logged'
        : 'Logged as an AI estimate until it is reviewed';
    case 'fix':
      return request.unit === null
        ? 'People reported it from the meal card'
        : `People asked for 1 ${request.unit} of ${request.foodName ?? request.name}`;
  }
}

/** The line under "What people typed". */
export function typedSubOf(request: AdminFoodRequest): string {
  const from = ORIGIN_LABELS[request.origin];
  if (request.gymName !== null) return `${from} · ${request.gymName}`;
  if (request.kind === 'fix' && request.foodName !== null && request.foodName !== request.name) {
    return `${from} · got ${request.foodName}`;
  }
  return from;
}

/** What approving this request did, said once it is answered. */
export function outcomeOf(request: AdminFoodRequest): string {
  if (request.status === 'rejected') return request.rejectionReason ?? 'Rejected';
  if (request.status === 'open') return request.proposal;
  const food = request.resolvedFoodName ?? 'a food that has since been removed';
  switch (request.kind) {
    case 'name':
      return `Added as a name for ${food}`;
    case 'new_food':
      return request.resolvedFoodId === request.foodId
        ? `Kept the AI estimate, ${food}`
        : `Answered with ${food}`;
    case 'fix':
      return `Fixed ${food}`;
  }
}
